// @ts-nocheck
import { supabase } from '@/integrations/supabase/client';
import { 
  SignatureDocument, 
  SignatureAssinatura, 
  CreateSignatureDocumentRequest,
  SignatureFormData,
  SignatureVerificationData,
  SignatureStats,
  EmailConfirmationData,
  PDFSignatureData
} from '@/types/signature';
import { generateToken, generateHash } from '@/utils/signatureUtils';

export class SignatureService {
  // Criar novo documento para assinatura
  static async createDocument(data: CreateSignatureDocumentRequest): Promise<SignatureDocument> {
    // Gerar token único e verificar se não existe
    let token_publico: string;
    let attempts = 0;
    
    do {
      token_publico = generateToken();
      attempts++;
      
      // Verificar se o token já existe
      const existingDoc = await this.getDocumentByToken(token_publico);
      if (existingDoc) {
        console.warn(`Token ${token_publico} já existe, gerando novo...`);
        if (attempts > 5) {
          throw new Error('Não foi possível gerar um token único após 5 tentativas');
        }
      }
    } while (await this.getDocumentByToken(token_publico));
    
    const link_assinatura = `${window.location.origin}/assinar/${token_publico}`;
    
    // Temporariamente usar um ID fixo para teste
    const usuario_id = '00000000-0000-0000-0000-000000000000';

    // Preparar dados para inserção
    const insertData: any = {
      titulo: data.titulo,
      usuario_id: usuario_id,
      url_pdf_original: data.url_pdf_original,
      requer_confirmacao_email: data.requer_confirmacao_email,
      token_publico: token_publico,
      link_assinatura: link_assinatura,
      descricao: data.descricao || null,
      metadata: data.metadata || {},
      status: 'pendente' // Definir status explicitamente
    };

    // Validar tamanho dos campos
    if (insertData.titulo.length > 255) {
      throw new Error('Título muito longo (máximo 255 caracteres)');
    }
    
    if (insertData.link_assinatura.length > 500) {
      throw new Error('Link de assinatura muito longo (máximo 500 caracteres)');
    }

    // Só incluir data_expiracao se não for vazia
    if (data.data_expiracao && data.data_expiracao.trim() !== '') {
      insertData.data_expiracao = data.data_expiracao;
    }

    console.log('Dados para inserção:', JSON.stringify(insertData, null, 2));
    
    try {
      const { data: document, error } = await supabase
        .from('signature_documents')
        .insert(insertData)
        .select()
        .single();

      if (error) {
        console.error('Erro ao criar documento:', error);
        console.error('Dados que causaram erro:', insertData);
        console.error('Código do erro:', error.code);
        console.error('Mensagem do erro:', error.message);
        console.error('Detalhes do erro:', error.details);
        throw error;
      }
      
      console.log('Documento criado com sucesso:', document);
      return document;
    } catch (error) {
      console.error('Erro completo:', error);
      throw error;
    }
  }

  // Buscar documentos do usuário
  static async getUserDocuments(): Promise<SignatureDocument[]> {
    // Temporariamente buscar todos os documentos para teste
    const { data, error } = await supabase
      .from('signature_documents')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Erro ao buscar documentos:', error);
      throw error;
    }
    return data || [];
  }

  // Buscar documento por token público
  static async getDocumentByToken(token: string): Promise<SignatureDocument | null> {
    const { data, error } = await supabase
      .from('signature_documents')
      .select('*')
      .eq('token_publico', token)
      .single();

    if (error && error.code !== 'PGRST116') throw error;
    return data;
  }

  // Criar assinatura
  static async createSignature(
    documento_id: string, 
    formData: SignatureFormData
  ): Promise<SignatureAssinatura> {
    const token_confirmacao = generateToken();
    
    // Capturar IP e User Agent
    const ip = await this.getClientIP();
    const user_agent = navigator.userAgent;

    const { data: assinatura, error } = await supabase
      .from('signature_assinaturas')
      .insert({
        documento_id,
        nome_assinante: formData.nome_assinante,
        email_assinante: formData.email_assinante,
        ip,
        user_agent,
        assinatura_base64: formData.assinatura_base64,
        foto_base64: formData.foto_base64,
        token_confirmacao,
        status: 'aguardando',
        confirmado_via_email: false // Definir valor padrão
      })
      .select()
      .single();

    if (error) throw error;
    return assinatura;
  }

  // Confirmar assinatura via email
  static async confirmSignature(token: string): Promise<SignatureAssinatura> {
    const { data: assinatura, error } = await supabase
      .from('signature_assinaturas')
      .update({
        status: 'confirmado',
        confirmado_via_email: true,
        data_confirmacao: new Date().toISOString()
      })
      .eq('token_confirmacao', token)
      .select()
      .single();

    if (error) throw error;
    return assinatura;
  }

  // Buscar assinatura por token
  static async getSignatureByToken(token: string): Promise<SignatureAssinatura | null> {
    const { data, error } = await supabase
      .from('signature_assinaturas')
      .select('*')
      .eq('token_confirmacao', token)
      .single();

    if (error && error.code !== 'PGRST116') throw error;
    return data;
  }

  // Buscar dados de verificação
  static async getVerificationData(token: string): Promise<SignatureVerificationData | null> {
    const documento = await this.getDocumentByToken(token);
    if (!documento) return null;

    const { data: assinatura, error } = await supabase
      .from('signature_assinaturas')
      .select('*')
      .eq('documento_id', documento.id)
      .single();

    if (error && error.code !== 'PGRST116') throw error;
    if (!assinatura) return null;

    const is_valid = assinatura.status === 'confirmado' || assinatura.status === 'assinado';
    const verification_url = `${window.location.origin}/verificar/${token}`;

    return {
      documento,
      assinatura,
      is_valid,
      verification_url
    };
  }

  // Gerar estatísticas
  static async getStats(): Promise<SignatureStats> {
    // Temporariamente buscar todas as estatísticas para teste
    const { data: documentos } = await supabase
      .from('signature_documents')
      .select('status');

    const { data: assinaturas } = await supabase
      .from('signature_assinaturas')
      .select('status');

    const stats: SignatureStats = {
      total_documentos: documentos?.length || 0,
      documentos_pendentes: documentos?.filter(d => d.status === 'pendente').length || 0,
      documentos_assinados: documentos?.filter(d => d.status === 'assinado').length || 0,
      documentos_expirados: documentos?.filter(d => d.status === 'expirado').length || 0,
      assinaturas_aguardando: assinaturas?.filter(a => a.status === 'aguardando').length || 0,
      assinaturas_confirmadas: assinaturas?.filter(a => a.status === 'confirmado').length || 0
    };

    return stats;
  }

  // Deletar documento
  static async deleteDocument(id: string): Promise<void> {
    const { error } = await supabase
      .from('signature_documents')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }

  // Atualizar documento
  static async updateDocument(id: string, data: Partial<CreateSignatureDocumentRequest>): Promise<SignatureDocument> {
    const { data: document, error } = await supabase
      .from('signature_documents')
      .update(data)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return document;
  }

  // Buscar IP do cliente
  static async getClientIP(): Promise<string> {
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      return data.ip;
    } catch (error) {
      console.error('Erro ao obter IP:', error);
      return 'unknown';
    }
  }

  // Marcar documento como assinado
  static async markDocumentAsSigned(documento_id: string): Promise<void> {
    const { error } = await supabase
      .from('signature_documents')
      .update({ status: 'assinado' })
      .eq('id', documento_id);

    if (error) throw error;
  }

  // Atualizar URL do PDF assinado
  static async updateSignedPDF(assinatura_id: string, url_pdf_assinado: string, hash_pdf: string): Promise<void> {
    const { error } = await supabase
      .from('signature_assinaturas')
      .update({
        url_pdf_assinado,
        hash_pdf,
        status: 'assinado'
      })
      .eq('id', assinatura_id);

    if (error) throw error;
  }

  // Atualizar metadata da assinatura
  static async updateSignatureMetadata(assinatura_id: string, metadata: Record<string, any>): Promise<void> {
    const { error } = await supabase
      .from('signature_assinaturas')
      .update({
        metadata: metadata
      })
      .eq('id', assinatura_id);

    if (error) throw error;
  }
}
